import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, IconButton, Tooltip, Stack, alpha, useTheme,
  TablePagination, Skeleton, Paper, Switch,
} from '@mui/material';
import { Edit, Delete, Inbox, Visibility, ImageOutlined } from '@mui/icons-material';
import type { MasterSchema, MasterFieldDef } from '../../constants/masters';
import { getMasterTypeForField } from '../../constants/masterRefFields';
import { parseCommaSeparatedMasterCodes, resolveMasterRecordActive, resolveTransportMasterImageUrl, formatAvailabilityDaysOfWeek, VEHICLE_FLEET_MASTERS } from '../../constants/transportSubserviceMasters';
import { getMasterOptions } from '../../services/platform/masterService';
import { useZoneCodeMap } from './ZoneMasterSelect';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';
import { resolveAssetUrl } from '../../utils/assetUrl';

export interface MasterRecordRow {
  _id: string;
  code: string;
  name: string;
  description?: string;
  country?: string;
  state?: string;
  city?: string;
  district?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
  isActive?: boolean;
}

interface Props {
  rows: MasterRecordRow[];
  schema?: MasterSchema | null;
  masterTypeKey?: string;
  loading?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (row: MasterRecordRow) => void;
  onDelete: (row: MasterRecordRow) => void;
  canDelete?: (row: MasterRecordRow) => boolean;
  /** Optional view / drill-down action (e.g. eye icon for sub-categories). */
  onView?: (row: MasterRecordRow) => void;
  viewTooltip?: string;
  onToggleActive?: (row: MasterRecordRow, active: boolean) => void;
  togglingRowId?: string | null;
}

const headCellSx = {
  fontWeight: 650,
  fontSize: '0.68rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: 'action.hover',
  color: 'text.secondary',
  py: 1.35,
} as const;

const EMPTY_META_FIELDS: MasterFieldDef[] = [];

function isImageMetaField(field: MasterFieldDef) {
  if (field.type === 'image') return true;
  return ['image', 'imageUrl', 'icon', 'iconUrl', 'iconImage', 'logo', 'banner', 'thumbnail', 'photo', 'coverImage', 'highlightImageUrl'].includes(field.key);
}

function resolveMetaImageSrc(row: MasterRecordRow, field: MasterFieldDef): string {
  const direct = row.metadata?.[field.key];
  if (direct != null && String(direct).trim()) {
    return resolveAssetUrl(String(direct));
  }
  if (isImageMetaField(field)) {
    return resolveAssetUrl(resolveTransportMasterImageUrl(row.metadata));
  }
  return '';
}

function formatMetaValue(
  field: MasterFieldDef,
  value: unknown,
  zoneMap: Map<string, string>,
  masterMaps: Map<string, Map<string, string>>,
  masterTypeKey: string,
): string {
  if (value == null || value === '') return '—';
  if (field.key === 'daysOfWeek') {
    return formatAvailabilityDaysOfWeek(value);
  }
  if (field.key === 'zoneCode' || field.key.toLowerCase().includes('zone')) {
    const code = String(value);
    const name = zoneMap.get(code);
    return name ? `${name} (${code})` : code;
  }
  const refType = getMasterTypeForField(field, masterTypeKey);
  if (refType) {
    if (field.multiple) {
      const codes = parseCommaSeparatedMasterCodes(value);
      if (!codes.length) return '—';
      return codes.map((code) => {
        const name = masterMaps.get(refType)?.get(code);
        return name ? `${name} (${code})` : code;
      }).join(', ');
    }
    const code = String(value);
    const name = masterMaps.get(refType)?.get(code);
    return name ? `${name} (${code})` : code;
  }
  return String(value);
}

function useMasterRefMaps(metaFields: MasterFieldDef[], masterTypeKey: string) {
  const types = useMemo(() => {
    const set = new Set<string>();
    for (const f of metaFields) {
      const mt = getMasterTypeForField(f, masterTypeKey);
      if (mt) set.add(mt);
    }
    return [...set];
  }, [metaFields, masterTypeKey]);

  const results = useQueries({
    queries: types.map((t) => ({
      queryKey: ['master-options', t],
      queryFn: () => getMasterOptions(t),
      staleTime: 60_000,
    })),
  });

  return useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    types.forEach((t, i) => {
      const codeMap = new Map<string, string>();
      (results[i]?.data || []).forEach((o) => {
        codeMap.set(o.code, o.name);
        codeMap.set(o.code.toUpperCase(), o.name);
      });
      map.set(t, codeMap);
    });
    return map;
  }, [types, results]);
}

export default function MasterRecordTable({
  rows,
  schema,
  masterTypeKey = '',
  loading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  canDelete,
  onView,
  viewTooltip = 'View',
  onToggleActive,
  togglingRowId,
}: Props) {
  const theme = useTheme();
  const { t } = useI18n();
  const zoneMap = useZoneCodeMap();
  const tl = (s: string) => translateUiText(s, t);
  const showGeo = schema?.geoFields?.length;
  const metaFields = schema?.metadataFields ?? EMPTY_META_FIELDS;
  const keyMetaFields = useMemo(() => {
    const fields = metaFields.filter((f) => f.key !== 'status' && f.key !== 'description');
    const imageFields = fields.filter(isImageMetaField);
    const otherFields = fields.filter((f) => !isImageMetaField(f));
    const merged = [...imageFields, ...otherFields];
    if (
      (VEHICLE_FLEET_MASTERS as readonly string[]).includes(masterTypeKey)
      && !imageFields.length
    ) {
      return [
        { key: 'imageUrl', label: 'Image', type: 'image' as const },
        ...otherFields,
      ].slice(0, 3);
    }
    return merged.slice(0, 3);
  }, [metaFields, masterTypeKey]);
  const showStatusToggle = !!onToggleActive;
  const masterMaps = useMasterRefMaps(keyMetaFields, masterTypeKey);

  const frameSx = {
    borderRadius: 2,
    border: `1px solid ${alpha('#0f172a', 0.08)}`,
    overflow: 'hidden',
    bgcolor: 'background.paper',
  } as const;

  if (loading) {
    return (
      <Paper elevation={0} sx={frameSx}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} height={44} sx={{ mx: 2, my: 0.5, borderRadius: 1 }} />
        ))}
      </Paper>
    );
  }

  if (!rows.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          ...frameSx,
          borderStyle: 'dashed',
          py: 7,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Inbox sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">{tl('No records yet')}</Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
          {tl('Click Add to create the first entry')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={frameSx}>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {[
                <TableCell key="#" sx={{ ...headCellSx, width: 52 }}>#</TableCell>,
                <TableCell key="name" sx={headCellSx}>{tl('Name')}</TableCell>,
                <TableCell key="code" sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>{tl('Code')}</TableCell>,
                ...(showGeo && schema?.geoFields?.includes('country')
                  ? [<TableCell key="country" sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>{tl('Country')}</TableCell>]
                  : []),
                ...keyMetaFields.map((f) => (
                  <TableCell
                    key={f.key}
                    sx={{
                      ...headCellSx,
                      display: { xs: 'none', [isImageMetaField(f) ? 'sm' : 'lg']: 'table-cell' },
                    }}
                  >
                    {tl(f.label)}
                  </TableCell>
                )),
                <TableCell key="status" sx={{ ...headCellSx, width: showStatusToggle ? 120 : 72 }}>
                  {tl('Status')}
                </TableCell>,
                <TableCell key="actions" sx={{ ...headCellSx, width: onView ? 108 : 72 }} align="right">{tl('Actions')}</TableCell>,
              ]}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const active = resolveMasterRecordActive(row);
              const serialNo = page * pageSize + index + 1;
              return (
                <TableRow
                  key={row._id}
                  hover
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    cursor: 'pointer',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                  }}
                  onClick={() => onEdit(row)}
                >
                  {[
                    <TableCell key="#">
                      <Typography variant="body2" fontWeight={600} color="text.secondary">{serialNo}</Typography>
                    </TableCell>,
                    <TableCell key="name">
                      <Typography variant="body2" fontWeight={700}>{row.name}</Typography>
                      {row.description ? (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 240, display: 'block' }}>
                          {row.description}
                        </Typography>
                      ) : null}
                    </TableCell>,
                    <TableCell key="code" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">{row.code}</Typography>
                    </TableCell>,
                    ...(showGeo && schema?.geoFields?.includes('country')
                      ? [
                          <TableCell key="country" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="caption">{row.country || '—'}</Typography>
                          </TableCell>,
                        ]
                      : []),
                    ...keyMetaFields.map((f) => {
                      const raw = row.metadata?.[f.key];
                      if (isImageMetaField(f)) {
                        const src = resolveMetaImageSrc(row, f);
                        return (
                          <TableCell key={f.key} sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {src ? (
                              <Box
                                component="img"
                                src={src}
                                alt={row.name}
                                sx={{
                                  width: 96,
                                  height: 44,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: 'divider',
                                  display: 'block',
                                  bgcolor: 'action.hover',
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 96,
                                  height: 44,
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: 'divider',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'action.hover',
                                  color: 'text.disabled',
                                }}
                              >
                                <ImageOutlined sx={{ fontSize: 18 }} />
                              </Box>
                            )}
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={f.key} sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {formatMetaValue(f, raw, zoneMap, masterMaps, masterTypeKey)}
                          </Typography>
                        </TableCell>
                      );
                    }),
                    <TableCell key="status" onClick={(e) => e.stopPropagation()}>
                      {showStatusToggle ? (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Chip
                            label={active ? 'Active' : 'Inactive'}
                            size="small"
                            color={active ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }}
                          />
                          <Switch
                            size="small"
                            checked={active}
                            disabled={togglingRowId === row._id}
                            onChange={(ev) => onToggleActive?.(row, ev.target.checked)}
                            inputProps={{ 'aria-label': `${active ? 'Deactivate' : 'Activate'} ${row.name}` }}
                          />
                        </Stack>
                      ) : (
                        <Chip
                          label={active ? 'Active' : 'Off'}
                          size="small"
                          color={active ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700 }}
                        />
                      )}
                    </TableCell>,
                    <TableCell key="actions" align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" justifyContent="flex-end" spacing={0}>
                        {onView && (
                          <Tooltip title={viewTooltip}>
                            <IconButton size="small" onClick={() => onView(row)} aria-label={viewTooltip}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => onEdit(row)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        {(!canDelete || canDelete(row)) && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => onDelete(row)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>,
                  ]}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, p) => onPageChange(p)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{ borderTop: 1, borderColor: 'divider' }}
      />
    </Paper>
  );
}
